import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Text } from '@comicrelief/component-library';
import {
  FullWidthStyledInput,
  StyledButtonWithStates,
  StyledSelect,
  HorizontalLine,
  PostcodeSearchWrapper,
  ButtonWrapper,
  ReviewDetailsWrapper,
  AddressField,
  EditDetailsButton,
  UseOtherAddressWrapper,
  AddressFieldsWrapper
} from '../../components/global/Global.style';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { PostcodeLookupAddress } from '@/api/lookups/postcodeLookup';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { updateUserDetailsFields } from '@/features/userDetails/userDetailsSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';

import { setBillingSameAsPostal } from './payinSlice';
import { formFields, getFormResolver } from './payinBillingAddressFields';
import { mapLookupToAddress } from '@/utils/addressMappingUtils';

import StepBack from '@/components/stepBack/StepBack';
import CheckboxWithSvg from '@/components/common/CheckboxWithSvg';
import PrivacyNotice from '@/components/privacyNotice/PrivacyNotice';
import PostcodeLookup from '@/components/postcodeLookup/PostcodeLookup';
import PageHeader from '@//components/pageHeader/PageHeader';

import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { setProvider } from '../paymentSelect/paymentSelectSlice';
import { getPageHeaderInitialText, getPageHeaderSupplementaryText } from '@/utils/paymentTextUtils';

interface PayinBillingAddressProps {
  totalSteps: number;
  currencySymbol: string;
}

function PayinBillingAddress({
  totalSteps,
  currencySymbol,
}: PayinBillingAddressProps) {
  const dispatch = useAppDispatch();
  const userDetailsState = useAppSelector(state => state.userDetails);
  const payinState = useAppSelector(state => state.payin);

  const journeyNavigate = useJourneyNavigate();
  const navigate = useNavigate();

  const { isDependencyEnabled, isProviderAvailable } = usePaymentsConfig();
  const lookupsEnabled = isDependencyEnabled('plus'); // "plus" is an old name for the "lookup" service

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: getFormResolver(),
    defaultValues: {
      billingSameAsPostal: payinState.billingSameAsPostal
    }
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: {
      errors: formErrors
    },
  } = formMethods;

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) {
        return;
      }
      dispatch(updateUserDetailsFields({
        [name]: formValues[name],
      }));
    });
    return () => watchInputs.unsubscribe();
  }, [watch, payinState, dispatch]);

  useEffect(() => {
    const stripeAvailable = isProviderAvailable('single', 'STRIPE');
    const braintreeAvailable = isProviderAvailable('single', 'BRAINTREE');
    if (stripeAvailable) {
      dispatch(setProvider('STRIPE'));
    } else if (braintreeAvailable) {
      dispatch(setProvider('BRAINTREE'));
    } else {
      logger.error('No card provider is available to accept PayIn');
      void navigate('/error');
    }
  }, [dispatch, isProviderAvailable, navigate]);

  const onPostcodeLookupSelect = (result: PostcodeLookupAddress) => {
    try {
      const fields = mapLookupToAddress(result);

      dispatch(updateUserDetailsFields(fields));

      Object.entries(fields).forEach(([field, value]) => {
        setValue(field, value as string, { shouldValidate: true });
      });
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Error mapping postcode lookup result');
    }
  };

  const handleUsePostalAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update state based on checkbox status:
    const isBillingSameAsPostal = event.target.checked;
    dispatch(setBillingSameAsPostal(isBillingSameAsPostal));

    // Since we're either passing the previously-supplied address details
    // or just resetting them, we can define this dynamically first:
    const billingAddressMapping = {
      address1: isBillingSameAsPostal ? payinState.address1 : '',
      address2: isBillingSameAsPostal ? payinState.address2 : '',
      address3: isBillingSameAsPostal ? payinState.address3 : '',
      town: isBillingSameAsPostal ? payinState.town : '',
      postcode: isBillingSameAsPostal ? payinState.postcode : '',
      country: isBillingSameAsPostal ? payinState.country : '',
    };

    // Then step through the object, updating the fields, and in turn, the state:
    Object.entries(billingAddressMapping).forEach(([field, value]) => {
      setValue(field, value);
    });
  };

  const onSubmit = () => {
    journeyNavigate('next');
  };

  const handleEditPostalAddress = () => {
    // Navigate back to the previous page (user-details)
    journeyNavigate('back');
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          onSubmit={event => void handleSubmit(onSubmit)(event)}
          noValidate
        >
          <PageHeader
            currentStep={4}
            totalSteps={totalSteps}
            headerText="Your billing address"
            initialText={getPageHeaderInitialText({ isPayIn: true })}
            supplementaryText={getPageHeaderSupplementaryText({ isPayIn: true, payinState })}
            currencySymbol={currencySymbol}
          />

          <UseOtherAddressWrapper>
            <CheckboxWithSvg
              checked={payinState.billingSameAsPostal}
              htmlFor={formFields.billingSameAsPostal.id}
              dataTest={formFields.billingSameAsPostal.id}
              svgFilePath="/assets/images/location.svg"
              onChange={handleUsePostalAddressChange}
              hiddenCheckboxId={formFields.billingSameAsPostal.id}
            >
              Use Postal Address
            </CheckboxWithSvg>
          </UseOtherAddressWrapper>

          {!payinState.billingSameAsPostal ? (
            <>
              <HorizontalLine $margin="m" />
              <Text tag="h4" family="Montserrat" color="black">
                Add a new billing address
              </Text>

              <StyledSelect
                label={formFields.country.label}
                id={formFields.country.id}
                options={formFields.country.options}
                errorMsg={formErrors?.country?.message}
                {...register(formFields.country.id)}
              />

              {(userDetailsState.country === 'GB' && lookupsEnabled) ? (
                <>
                  <PostcodeSearchWrapper>
                    <PostcodeLookup onSelect={onPostcodeLookupSelect} />
                  </PostcodeSearchWrapper>

                  <HorizontalLine $margin="m" />
                  <Text tag="h4" family="Montserrat" color="black">
                    Or enter your billing address manually
                  </Text>
                </>
              ) : (
                <HorizontalLine $margin="m" />
              )}

              <AddressFieldsWrapper>
                <FullWidthStyledInput
                  label={formFields.address1.label}
                  id={formFields.address1.id}
                  type={formFields.address1.type}
                  errorMsg={formErrors?.address1?.message}
                  {...register(formFields.address1.id)}
                />

                <FullWidthStyledInput
                  label={formFields.address2.label}
                  id={formFields.address2.id}
                  type={formFields.address2.type}
                  errorMsg={formErrors?.address2?.message}
                  optional
                  {...register(formFields.address2.id)}
                />

                <FullWidthStyledInput
                  label={formFields.address3.label}
                  id={formFields.address3.id}
                  type={formFields.address3.type}
                  errorMsg={formErrors?.address3?.message}
                  optional
                  {...register(formFields.address3.id)}
                />

                <FullWidthStyledInput
                  label={formFields.town.label}
                  id={formFields.town.id}
                  type={formFields.town.type}
                  errorMsg={formErrors?.town?.message}
                  {...register(formFields.town.id)}
                />

                <FullWidthStyledInput
                  label={formFields.postcode.label}
                  id={formFields.postcode.id}
                  type={formFields.postcode.type}
                  errorMsg={formErrors?.postcode?.message}
                  {...register(formFields.postcode.id)}
                />
              </AddressFieldsWrapper>
            </>
          ) : (
            <ReviewDetailsWrapper data-test="reviewBillingAddress">

              <AddressField data-test="payin-billing-address-line1">{payinState.address1}</AddressField>

              {payinState.address2 && <AddressField data-test="payin-billing-address-line2">{payinState.address2}</AddressField>}

              {payinState.address3 && <AddressField data-test="payin-billing-address-line3">{payinState.address3}</AddressField>}

              <AddressField data-test="payin-billing-address-town">{payinState.town}</AddressField>

              <AddressField data-test="payin-billing-address-postcode">{payinState.postcode}</AddressField>

              <EditDetailsButton
                type="button"
                color="white"
                onClick={handleEditPostalAddress}
                data-test="editPostalAddress"
                fullWidth
              >
                Edit postal address
              </EditDetailsButton>
            </ReviewDetailsWrapper>
          )}

          <PrivacyNotice />

          <PaymentErrorText />

          <ButtonWrapper>

            <StyledButtonWithStates
              type="submit"
              fullWidth
              data-test="payinBillingAddressContinue"
            >
              Continue
            </StyledButtonWithStates>
          </ButtonWrapper>

        </form>
      </FormProvider>
      <StepBack data-test="payinBillingAddressGoBack" />
    </>
  );
}

export default PayinBillingAddress;
